import React, { useContext, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Ticket,
  ClipboardList,
  Hash,
  Users,
  Settings,
  Mail,
  QrCode,
  X,
  Moon,
  Sun,
  Home,
  LogOut,
  Trophy,
} from 'lucide-react';
import { Store } from '../../Store';
import { cn } from '../../lib/utils';
import { paths } from '../../routes/paths';

const NAV_ITEMS = [
  { to: paths.admin.root, label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: paths.admin.rifas, label: 'Bonos', icon: Ticket },
  { to: paths.admin.reservas, label: 'Reservas', icon: ClipboardList },
  { to: paths.admin.tickets, label: 'Tickets', icon: Hash },
  { to: paths.admin.liveDraw, label: 'Sorteo en vivo', icon: Trophy },
  { to: paths.admin.redeem, label: 'Validar ticket', icon: QrCode },
  { to: paths.admin.users, label: 'Usuarios', icon: Users },
  { to: paths.admin.newsletter, label: 'Newsletter', icon: Mail },
  { to: paths.admin.config, label: 'Configuración', icon: Settings },
];

/**
 * Sidebar navigation — themed with light/dark mode.
 * Light mode: white card surface, muted text, primary accent on active.
 * Dark mode : navy (#1a2035) bg, slate text, white highlight on active.
 * Footer contains: theme toggle, go-to-site link, user info, sign-out.
 */
export default function AdminSidebar({ companyName, logoURL, onNavigate, onClose }) {
  const { state, dispatch } = useContext(Store);
  const [failedLogoURL, setFailedLogoURL] = useState(null);
  const navigate = useNavigate();
  const userInfo = state?.userInfo;
  const isDark = state?.theme === 'dark';
  const brandName = companyName?.trim() || 'Administración';

  const toggleTheme = () => dispatch({ type: 'SET_THEME', payload: isDark ? 'light' : 'dark' });

  const signOut = () => {
    dispatch({ type: 'USER_SIGNOUT' });
    localStorage.removeItem('userInfo');
    navigate('/signin');
  };

  const initials = userInfo?.nombre
    ? userInfo.nombre
        .split(' ')
        .map(p => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?';

  return (
    <aside
      className={cn(
        'flex h-full w-64 flex-col',
        'bg-card dark:bg-[#1a2035]',
        'border-r border-border dark:border-white/[0.08]'
      )}
    >
      {/* Brand */}
      <div className='flex h-16 shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-4 dark:border-slate-700'>
        <div className='flex min-w-0 flex-1 items-center gap-2'>
          {logoURL && logoURL !== failedLogoURL && (
            <img
              src={logoURL}
              alt=''
              className='h-8 w-8 shrink-0 object-contain'
              onError={() => setFailedLogoURL(logoURL)}
            />
          )}
          <span
            title={brandName}
            className='truncate text-base font-bold tracking-tight text-foreground dark:text-white'
          >
            {brandName}
          </span>
        </div>
        {onClose && (
          <button
            type='button'
            onClick={onClose}
            aria-label='Cerrar menú'
            className='inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white lg:hidden'
          >
            <X className='h-5 w-5' />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className='flex-1 overflow-y-auto py-3'>
        <ul className='space-y-0.5 px-2'>
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium no-underline transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary dark:bg-white/[0.15] dark:text-white'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-100'
                    )
                  }
                >
                  <Icon className='h-5 w-5 shrink-0' />
                  <span className='truncate'>{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer controls */}
      <div className='shrink-0 border-t border-slate-200 pb-4 pt-3 dark:border-slate-700'>
        <div className='space-y-0.5 px-2'>
          {/* Theme toggle */}
          <button
            type='button'
            onClick={toggleTheme}
            className='flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-100'
          >
            {isDark ? <Sun className='h-5 w-5 shrink-0' /> : <Moon className='h-5 w-5 shrink-0' />}
            <span>{isDark ? 'Modo claro' : 'Modo oscuro'}</span>
          </button>

          {/* Go to site */}
          <NavLink
            to='/'
            className='flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground no-underline transition-colors hover:bg-accent hover:text-foreground dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-100'
          >
            <Home className='h-5 w-5 shrink-0' />
            <span>Ir al Inicio</span>
          </NavLink>

          {/* User info + sign-out */}
          <div className='mt-1 rounded-lg bg-muted px-3 py-2.5 dark:bg-slate-800/50'>
            <div className='flex items-center gap-3'>
              <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground'>
                {initials}
              </span>
              <div className='min-w-0 flex-1'>
                <p className='truncate text-sm font-medium leading-tight text-foreground dark:text-white'>
                  {userInfo?.nombre || 'Admin'}
                </p>
                {userInfo?.email && (
                  <p className='truncate text-xs leading-tight text-muted-foreground dark:text-slate-400'>
                    {userInfo.email}
                  </p>
                )}
              </div>
            </div>
            <button
              type='button'
              onClick={signOut}
              className='mt-2.5 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10 dark:text-red-400 dark:hover:bg-red-500/15 dark:hover:text-red-300'
            >
              <LogOut className='h-4 w-4 shrink-0' />
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
